import { ApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  Booking,
  PaginatedBookingResponse,
  BookingNoShowSummary,
} from "@/types/booking-management.types";
import { RefundMode } from "@/constant";

export const ServiceProviderBookingService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all service bookings (general listing with filters)
    getServiceBookings: builder.query<
      PaginatedBookingResponse,
      {
        page?: number;
        limit?: number;
        status?: number;
        search?: string;
        harbor_id?: string;
        service_provider_id?: string;
      }
    >({
      query: (params) => {
        // Determine the URL based on harbor_id or service_provider_id
        let url = "service-bookings/pending-requests";

        if (params.harbor_id) {
          url = `service-bookings/my-bookings/harbor/${params.harbor_id}`;
        } else if (params.service_provider_id) {
          url = `service-bookings/provider/${params.service_provider_id}`;
        }

        return {
          url,
          method: "GET",
          params: {
            page: params.page,
            limit: params.limit,
            status: params.status,
            search: params.search,
            harbor_id: params.harbor_id,
          },
        };
      },
      transformResponse: (response: PaginatedBookingResponse & { pagination?: { total: number; page: number; limit: number; totalPages: number } }) => {
        if (response.pagination) {
          return { ...response, ...response.pagination };
        }
        return response;
      },
      providesTags: ["ServiceProviderBookings"],
    }),

    // Get pending service booking requests
    getPendingServiceBookingRequests: builder.query<
      PaginatedBookingResponse,
      {
        page?: number;
        limit?: number;
        harbor_id?: string;
        service_provider_id?: string;
      }
    >({
      query: (params) => ({
        url: "service-bookings/pending-requests",
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          harbor_id: params.harbor_id,
          service_provider_id: params.service_provider_id,
        },
      }),
      transformResponse: (response: PaginatedBookingResponse) => response,
      providesTags: ["ServiceProviderBookings"],
    }),

    // Approve a service booking
    approveServiceBooking: builder.mutation<ApiResponse<Booking>, string>({
      query: (id) => ({
        url: `service-bookings/${id}/approve`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ["ServiceProviderBookings"],
    }),

    // Reject a service booking
    rejectServiceBooking: builder.mutation<ApiResponse<Booking>, string>({
      query: (id) => ({
        url: `service-bookings/${id}/reject`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ["ServiceProviderBookings"],
    }),

    // Service booking no-show summary
    getServiceBookingNoShowSummary: builder.mutation<
      ApiResponse<BookingNoShowSummary>,
      string
    >({
      query: (id) => ({
        url: `service-bookings/${id}/no-show-summary`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<BookingNoShowSummary>) =>
        response,
    }),

    // Mark service booking as no-show
    markServiceBookingNoShow: builder.mutation<
      ApiResponse<Booking>,
      { id: string; data: { mode: RefundMode; note: string } }
    >({
      query: ({ id, data }) => ({
        url: `service-bookings/${id}/no-show`,
        method: "POST",
        data,
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ["ServiceProviderBookings"],
    }),

    // Get all service bookings for a service provider (Legacy/Specific)
    getAllServiceProviderBookings: builder.query<
      PaginatedBookingResponse,
      {
        page?: number;
        limit?: number;
        status?: number;
        search?: string;
        service_provider_id?: string;
      }
    >({
      query: (params) => ({
        url: `service-bookings/provider/${params.service_provider_id}`,
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
        },
      }),
      transformResponse: (response: PaginatedBookingResponse) => response,
      providesTags: ["ServiceProviderBookings"],
    }),

    // Get a service booking by ID
    getServiceProviderBookingById: builder.query<Booking, string>({
      query: (id) => ({
        url: `service-bookings/${id}`,
        method: "GET",
      }),
      transformResponse: (response: Booking) => response,
    }),

    // Update a service booking status
    updateServiceProviderBookingStatus: builder.mutation<
      ApiResponse<Booking>,
      {
        id: string;
        update: { status: number; cancellation_reason?: string };
      }
    >({
      query: ({ id, update }) => ({
        url: `service-bookings/${id}/status`,
        method: "PUT",
        data: update,
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ["ServiceProviderBookings"],
    }),

    // Update a service booking
    updateServiceProviderBooking: builder.mutation<
      ApiResponse<Booking & { conflictingBookings: boolean }>,
      {
        id: string;
        update: { start_date: string; end_date: string; status: number };
      }
    >({
      query: ({ id, update }) => ({
        url: `service-bookings/${id}`,
        method: "PUT",
        data: update,
      }),
      transformResponse: (
        response: ApiResponse<Booking & { conflictingBookings: boolean }>,
      ) => response,
      invalidatesTags: ["ServiceProviderBookings"],
    }),

    // Cancel a service booking
    cancelServiceProviderBooking: builder.mutation<
      ApiResponse<Booking>,
      { id: string; data: { status: number; cancellation_reason?: string } }
    >({
      query: ({ id }) => ({
        url: `service-bookings/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ["ServiceProviderBookings"],
    }),
  }),
});

export const {
  useGetAllServiceProviderBookingsQuery,
  useGetServiceProviderBookingByIdQuery,
  useUpdateServiceProviderBookingStatusMutation,
  useUpdateServiceProviderBookingMutation,
  useCancelServiceProviderBookingMutation,
  useGetServiceBookingsQuery,
  useGetPendingServiceBookingRequestsQuery,
  useApproveServiceBookingMutation,
  useRejectServiceBookingMutation,
  useGetServiceBookingNoShowSummaryMutation,
  useMarkServiceBookingNoShowMutation,
} = ServiceProviderBookingService;
